import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { SERVICE_URL } from '../../utils/api';

const STATUS = {
  pending: { label: 'รอเตรียม', color: '#8A938B' },
  acknowledged: { label: 'รับทราบแล้ว', color: '#8A938B' },
  room_ready: { label: 'ห้องพร้อม', color: '#2C6DAF' },
  guest_checkedin: { label: 'รออนุมัติ', color: '#B26B0E' },
  pending_approval: { label: 'รออนุมัติ', color: '#B26B0E' },
  approved: { label: 'อนุมัติแล้ว', color: '#0F8A56' },
  paid: { label: 'จ่ายแล้ว', color: '#0F8A56' },
  declined: { label: 'ไม่สะดวก', color: '#CD3E33' },
};
const statusOf = (s) => STATUS[s] || { label: s, color: '#8A938B' };
// แม่บ้านรายเดือน: ส่งงานแล้ว = รายงานแล้ว จบ (API ปิดงานเป็น payment_status = void ไม่มีอนุมัติ/จ่าย)
const jobStatusOf = (job) => (job.payment_status === 'void' && ['guest_checkedin', 'pending_approval'].includes(job.status)
  ? { label: 'รายงานแล้ว', color: '#0F8A56' }
  : statusOf(job.status));

// ระดับบริการระหว่างพัก (midstay) — แม่บ้านเลือกเอง
const MIDSTAY_LEVELS = [
  { v: 1, label: 'ลูกค้าไม่ให้ทำอะไร' },
  { v: 2, label: 'ทำความสะอาดด้านนอก + ห้องน้ำ' },
  { v: 3, label: '+ เปลี่ยนผ้าปู / ผ้าเช็ดตัว' },
  { v: 4, label: 'ทำความสะอาด + เปลี่ยนอุปกรณ์ครบ' },
];
const midstayLevelLabel = (v) => (MIDSTAY_LEVELS.find((l) => l.v === Number(v)) || {}).label || `ระดับ ${v}`;

export default function CleanerWork() {
  const search = new URLSearchParams(window.location.search);
  const token = search.get('t');
  const initialDate = search.get('date');

  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState('calendar'); // calendar | day | job
  const [activeMonth, setActiveMonth] = useState(initialDate ? new Date(initialDate) : new Date());
  const [monthBookings, setMonthBookings] = useState([]); // booking ทั้งเดือน (occupancy) อิงห้องของแม่บ้าน
  const [selectedDate, setSelectedDate] = useState(initialDate || null);
  const [job, setJob] = useState(null);
  const [phase, setPhase] = useState('prep');
  const [template, setTemplate] = useState([]);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [midstayLevel, setMidstayLevel] = useState(null); // ระดับบริการที่แม่บ้านเลือก (midstay)
  const [midstayNote, setMidstayNote] = useState('');

  const api = axios.create({ baseURL: SERVICE_URL });
  const cw = useCallback((path, opts = {}) => {
    const sep = path.includes('?') ? '&' : '?';
    return api({ url: `cleaner-web/${path}${sep}t=${encodeURIComponent(token || '')}`, ...opts });
    // eslint-disable-next-line
  }, [token]);

  const loadCalendar = useCallback((monthDate) => {
    const month = moment(monthDate).format('YYYY-MM');
    cw(`my-overview?month=${month}`).then((r) => {
      setMonthBookings(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
  }, [cw]);

  const openDay = useCallback((date) => {
    setSelectedDate(date);
    setView('day');
  }, []);

  useEffect(() => {
    if (!token) { setError('ลิงก์ไม่ถูกต้อง (ไม่มีรหัส)'); return; }
    cw('me').then((r) => {
      setMe(r.data);
      loadCalendar(activeMonth);
      if (initialDate) openDay(initialDate);
    }).catch(() => setError('ลิงก์ไม่ถูกต้องหรือหมดอายุ กรุณาเปิดจากลิงก์ในไลน์อีกครั้ง'));
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (token && me) loadCalendar(activeMonth); }, [activeMonth]); // eslint-disable-line

  const openJob = (id) => {
    cw(`job/${id}`).then((r) => {
      setJob(r.data.job); setPhase(r.data.phase); setTemplate(r.data.checklist_template || []);
      const init = {};
      (r.data.checklist_template || []).forEach((it) => { init[it.id] = { result: 'pass', note: '', photo_url: '' }; });
      setChecks(init);
      setMidstayLevel(r.data.job?.service_level || null); // ถ้าเคยส่งแล้วจะโชว์ระดับเดิม
      setMidstayNote(r.data.job?.cleaner_note || '');
      setView('job');
    }).catch(() => {});
  };

  const acknowledge = (id) => cw(`job/${id}/acknowledge`, { method: 'post' }).then(() => { loadCalendar(activeMonth); openDay(selectedDate); }).catch(() => {});

  // ----- derive จาก monthBookings: ปฏิทิน occupancy + รายการของวันที่เลือก -----
  const calMap = useMemo(() => {
    const map = {};
    const bump = (ds, key) => { if (!map[ds]) map[ds] = { occ: 0, arrivals: 0, checkouts: 0 }; map[ds][key] += 1; };
    monthBookings.forEach((b) => {
      if (!b.start_date || !b.end_date) return;
      let d = moment(b.start_date); const end = moment(b.end_date); let guard = 0;
      while (d.isSameOrBefore(end, 'day') && guard < 120) {
        const ds = d.format('YYYY-MM-DD');
        bump(ds, 'occ');
        if (ds === b.start_date) bump(ds, 'arrivals'); // แขกเข้าใหม่วันนั้น = ต้องเตรียมห้อง
        d = d.add(1, 'day'); guard += 1;
      }
      bump(moment(b.end_date).add(1, 'days').format('YYYY-MM-DD'), 'checkouts'); // วันเช็คเอาท์ = end_date + 1 (มาเก็บห้อง)
    });
    return map;
  }, [monthBookings]);

  const dayBookings = useMemo(() => {
    if (!selectedDate) return [];
    return monthBookings
      .filter((b) => {
        if (!b.start_date || !b.end_date) return false;
        const co = moment(b.end_date).add(1, 'days').format('YYYY-MM-DD');
        return (b.start_date <= selectedDate && selectedDate <= b.end_date) || selectedDate === co; // พักอยู่ หรือ เช็คเอาท์วันนี้
      })
      // เช็คเอาท์ (เก็บห้องคนเก่า) ขึ้นก่อน แล้วค่อยงานเข้าพัก/เตรียมห้องใหม่ · จากนั้นเรียงตามชื่อห้อง
      .sort((a, b) => {
        const co = (x) => (moment(x.end_date).add(1, 'days').format('YYYY-MM-DD') === selectedDate ? 0 : 1);
        return (co(a) - co(b)) || (a.room?.name_th || '').localeCompare(b.room?.name_th || '');
      });
  }, [monthBookings, selectedDate]);

  const setResult = (id, result) => setChecks((c) => ({ ...c, [id]: { ...c[id], result } }));
  const setNote = (id, note) => setChecks((c) => ({ ...c, [id]: { ...c[id], note } }));
  const onPickPhoto = (id, file) => {
    if (!file) return;
    setUploadingId(id);
    const fd = new FormData(); fd.append('file', file);
    cw('upload-photo', { method: 'post', data: fd, headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => setChecks((c) => ({ ...c, [id]: { ...c[id], photo_url: r.data.url } })))
      .catch(() => {}).finally(() => setUploadingId(null));
  };

  const submit = () => {
    if (!job || busy) return;
    if (phase === 'midstay') return submitMidstay();
    setBusy(true);
    const checklist = template.map((it) => ({
      item_id: it.id, label: it.label,
      result: checks[it.id]?.result || 'pass',
      note: checks[it.id]?.note || '',
      photo_url: checks[it.id]?.photo_url || '',
    }));
    const ep = phase === 'checkin' ? 'checkin' : 'prep';
    cw(`job/${job.id}/${ep}`, { method: 'post', data: { checklist } })
      .then(() => { setBusy(false); loadCalendar(activeMonth); openDay(selectedDate); })
      .catch(() => setBusy(false));
  };

  // ส่งงาน midstay: เลือกระดับ + เฉพาะ checklist ที่อยู่ในระดับนั้น + โน้ต
  const submitMidstay = () => {
    if (!midstayLevel || busy) return;
    setBusy(true);
    const items = template.filter((it) => (Number(it.service_level) || 99) <= midstayLevel);
    const checklist = items.map((it) => ({
      item_id: it.id, label: it.label,
      result: checks[it.id]?.result || 'pass',
      note: checks[it.id]?.note || '',
      photo_url: checks[it.id]?.photo_url || '',
    }));
    cw(`job/${job.id}/midstay`, { method: 'post', data: { service_level: midstayLevel, checklist, note: midstayNote } })
      .then(() => { setBusy(false); loadCalendar(activeMonth); openDay(selectedDate); })
      .catch(() => setBusy(false));
  };

  const anyFail = template.some((it) => checks[it.id]?.result === 'fail');

  // ---------- styles ----------
  const S = {
    // paddingTop เผื่อ header ของ LINE in-app browser ที่อยู่ชิดด้านบน (แยก property กัน shorthand+comma พัง)
    page: { boxSizing: 'border-box', width: '100%', maxWidth: 480, margin: '0 auto', paddingTop: 76, paddingRight: 16, paddingBottom: 48, paddingLeft: 16, fontFamily: 'inherit', color: '#1A211D', minHeight: '100vh', overflowX: 'hidden' },
    bar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
    back: { flexShrink: 0, border: '1px solid #DBE2DC', background: '#fff', borderRadius: 10, padding: '8px 12px', fontWeight: 700, color: '#0F8A56', whiteSpace: 'nowrap' },
    h: { fontSize: 20, fontWeight: 800, margin: 0 },
    sub: { fontSize: 13, color: '#586159' },
    card: { boxSizing: 'border-box', border: '1.5px solid #DBE2DC', borderRadius: 14, padding: 16, marginBottom: 12, background: '#fff' },
    bigBtn: (bg) => ({ boxSizing: 'border-box', width: '100%', border: 'none', borderRadius: 16, padding: 20, fontSize: 20, fontWeight: 800, color: '#fff', background: bg, marginTop: 12 }),
    toggleWrap: { display: 'flex', border: '1.5px solid #CBD4CC', borderRadius: 999, overflow: 'hidden' },
    tg: (on, kind) => ({ border: 'none', padding: '10px 16px', fontSize: 16, fontWeight: 800, minWidth: 56, background: on ? (kind === 'pass' ? '#0F8A56' : '#B26B0E') : 'transparent', color: on ? '#fff' : '#8A938B' }),
    pill: (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, color: c, background: c + '22' }),
  };

  if (error) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <p style={{ marginTop: 12, color: '#586159' }}>{error}</p>
    </div>;
  }
  if (!me) return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80, color: '#586159' }}>กำลังโหลด…</div>;

  // ---------- CALENDAR ----------
  if (view === 'calendar') {
    return (
      <div style={S.page}>
        <style>{`.react-calendar{width:100%!important;max-width:100%;border:none;font-family:inherit}`}</style>
        <div style={{ marginBottom: 10 }}>
          <h1 style={S.h}>สวัสดีค่ะ พี่{me.firstname} 🧹</h1>
          <div style={S.sub}>ปฏิทินงานของฉัน — แตะวันที่มีตัวเลขเพื่อดูรายละเอียด</div>
        </div>
        <Calendar
          locale="th-TH"
          activeStartDate={activeMonth}
          onActiveStartDateChange={({ activeStartDate }) => activeStartDate && setActiveMonth(activeStartDate)}
          onClickDay={(d) => { const ds = moment(d).format('YYYY-MM-DD'); if (calMap[ds]) openDay(ds); }}
          tileContent={({ date, view: v }) => {
            if (v !== 'month') return null;
            const ds = moment(date).format('YYYY-MM-DD');
            const info = calMap[ds];
            if (!info || (info.occ === 0 && info.checkouts === 0)) return null;
            // จุดเล็ก: ส้ม = เข้าใหม่ (เตรียมห้อง) · เทา = พักต่อ · ฟ้า = เช็คเอาท์ (เก็บห้อง)
            const dot = (c, key) => <span key={key} style={{ width: 6, height: 6, borderRadius: 999, background: c, display: 'inline-block' }} />;
            return <div style={{ marginTop: 3, display: 'flex', gap: 3, justifyContent: 'center' }}>
              {info.checkouts > 0 && dot('#2C6DAF', 'co') /* เช็คเอาท์ก่อน (ต้องเก็บห้องคนเก่าก่อนเตรียมห้องใหม่) */}
              {info.occ > 0 && dot(info.arrivals > 0 ? '#B26B0E' : '#8A938B', 'occ')}
            </div>;
          }}
          value={null}
        />
        <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: '#586159' }}>
          {[['เข้าใหม่', '#B26B0E'], ['พักต่อ', '#8A938B'], ['เช็คเอาท์', '#2C6DAF']].map(([l, c]) => (
            <span key={l}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: c, marginRight: 5 }} />{l}</span>
          ))}
        </div>
      </div>
    );
  }

  // ---------- DAY ----------
  if (view === 'day') {
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={() => setView('calendar')}>‹ ปฏิทิน</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>งานวันที่ {moment(selectedDate).format('D MMM')}</h1><div style={S.sub}>เฉพาะห้องของฉัน</div></div>
        </div>
        {dayBookings.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ไม่มีงานวันนี้ (ไม่มีเข้าพัก/เช็คเอาท์)</div>}
        {dayBookings.map((b) => {
          const checkoutDate = moment(b.end_date).add(1, 'days').format('YYYY-MM-DD'); // วันเช็คเอาท์จริง = end_date + 1
          const isCheckoutDay = selectedDate === checkoutDate; // วันนี้แขกออก = มาเก็บห้อง
          const isArrival = !isCheckoutDay && b.start_date === selectedDate;   // แขกเข้าวันนี้ = ต้องเตรียมห้อง
          const isLastNight = !isCheckoutDay && b.end_date === selectedDate;   // คืนสุดท้าย (ออกพรุ่งนี้)
          const guestQty = Number(b.guest_qty || 0);
          const addQty = Number(b.additional_guest_qty || 0) + Number(b.additional_baby_qty || 0);

          // ---- การ์ดวันเช็คเอาท์ (เก็บห้องหลังแขกออก) — ข้อมูลแจ้งเตือน ไม่มี job แยกให้เปิด ----
          if (isCheckoutDay) {
            return (
              <div key={`co-${b.id}`} style={{ ...S.card, borderColor: '#2C6DAF', background: '#EEF4FB' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{b.room?.name_th}</div>
                    <div style={{ fontSize: 14, color: '#2C6DAF', fontWeight: 800, marginTop: 2 }}>🧹 เก็บห้องหลังเช็คเอาท์</div>
                    <div style={{ fontSize: 12, color: '#586159', marginTop: 3 }}>
                      🔑 เข้า {moment(b.start_date).format('D MMM')} &nbsp;→&nbsp; 🧳 ออก {moment(checkoutDate).format('D MMM')}
                    </div>
                    <div style={{ fontSize: 12, color: '#8A938B', marginTop: 2 }}>แขกออกวันนี้ · {guestQty} ท่าน</div>
                  </div>
                  <span style={S.pill('#2C6DAF')}>เก็บห้อง</span>
                </div>
                {b.cleaner_custom_text && <div style={{ fontSize: 13, color: '#B26B0E', marginTop: 6 }}>❗️ {b.cleaner_custom_text}</div>}
              </div>
            );
          }

          // ---- การ์ดวันเข้าพัก (arrival) หรือ วันพักต่อ (midstay) ----
          const checkoutStr = moment(checkoutDate).format('D MMM');
          const isStaying = !isArrival;   // start < วันนี้ <= end -> วันบริการระหว่างพัก (midstay)
          const job = (b.cleaner_jobs || []).find((j) => j.service_date === selectedDate); // งานของวันนี้ (arrival/midstay)
          const st = job ? jobStatusOf(job) : null;
          const jobDone = job && ['guest_checkedin', 'pending_approval', 'approved', 'paid'].includes(job.status);
          const accent = isStaying ? '#0F8A56' : '#DBE2DC';
          return (
            <div key={b.id} style={{ ...S.card, borderColor: accent }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{b.room?.name_th}</div>
                  {isStaying && <div style={{ fontSize: 14, color: '#0F8A56', fontWeight: 800, marginTop: 2 }}>🧹 บริการระหว่างพัก{job && job.service_level ? ` · ${midstayLevelLabel(job.service_level)}` : ''}</div>}
                  <div style={{ fontSize: 13, color: '#586159', marginTop: 2 }}>
                    👥 {guestQty} ท่าน{addQty > 0 && <span style={{ color: '#B26B0E', fontWeight: 700 }}> ❗️เสริม {addQty}</span>}
                  </div>
                  {b.contact_name && <div style={{ fontSize: 12, color: '#8A938B', marginTop: 2 }}>👤 {b.contact_name} {b.contact_tel_1 || ''}</div>}
                  <div style={{ fontSize: 12, color: '#586159', marginTop: 3 }}>
                    🔑 เข้า {moment(b.start_date).format('D MMM')} &nbsp;→&nbsp; 🧳 ออก {checkoutStr}
                  </div>
                </div>
                {st ? <span style={S.pill(st.color)}>{st.label}</span>
                  : <span style={S.pill('#8A938B')}>ยังไม่เปิดงาน</span>}
              </div>

              {/* ป้ายบอกว่าวันที่เลือก เกิดอะไรกับ booking นี้ */}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {isArrival && <span style={{ ...S.pill('#B26B0E'), fontSize: 12 }}>🔑 เข้าพักวันนี้</span>}
                {isStaying && isLastNight && <span style={{ ...S.pill('#2C6DAF'), fontSize: 12 }}>🧳 คืนสุดท้าย · ออกพรุ่งนี้</span>}
                {isStaying && !isLastNight && <span style={{ ...S.pill('#8A938B'), fontSize: 12 }}>😴 แขกพักอยู่</span>}
              </div>
              {b.cleaner_custom_text && <div style={{ fontSize: 13, color: '#B26B0E', marginTop: 6 }}>❗️ {b.cleaner_custom_text}</div>}

              {/* ปุ่มเปิดงาน (เฉพาะเมื่อมี job แล้ว) */}
              {job && (
                <button style={{ ...S.bigBtn(jobDone ? '#586159' : '#0F8A56'), padding: 12, fontSize: 15, marginTop: 12 }}
                  onClick={() => openJob(job.id)}>
                  {jobDone ? 'ดูรายละเอียด / ประวัติ ›' : (isStaying ? 'เปิดบันทึกงาน (เลือกระดับ) ›' : 'เปิดเช็คห้อง ›')}
                </button>
              )}
              {job && job.status === 'pending' && isArrival && (
                <button style={{ ...S.bigBtn('#2C6DAF'), padding: 12, fontSize: 15, marginTop: 8 }}
                  onClick={() => acknowledge(job.id)}>รับทราบ</button>
              )}
              {!job && (
                <div style={{ fontSize: 12, color: '#8A938B', marginTop: 10, textAlign: 'center' }}>
                  {isArrival ? 'ระบบจะเปิดงานให้เมื่อใกล้ถึงวันเข้าพัก' : 'ระบบจะเปิดงานให้ตอนเช้า (08:00)'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ---------- JOB (รายละเอียด + ประวัติ + checklist) ----------
  const isMidstay = phase === 'midstay';
  const isPrep = phase !== 'checkin';
  const b = job?.booking || {};
  const guestQty = Number(b.guest_qty || 0);
  const additionalQty = Number(b.additional_guest_qty || 0) + Number(b.additional_baby_qty || 0);
  const totalQty = guestQty + additionalQty;
  const bNote = b.cleaner_custom_text || '';
  const history = job?.checklists || [];
  const jobDone = ['guest_checkedin', 'pending_approval', 'approved', 'paid'].includes(job?.status);
  // midstay: แสดง checklist เฉพาะที่อยู่ในระดับที่เลือก (ระดับ 1 = ไม่มีเลย)
  const visibleTemplate = isMidstay
    ? (midstayLevel ? template.filter((it) => (Number(it.service_level) || 99) <= midstayLevel) : [])
    : template;
  return (
    <div style={S.page}>
      <div style={S.bar}>
        <button style={S.back} onClick={() => openDay(selectedDate)}>‹ งานวันนั้น</button>
        <div>
          <h1 style={{ ...S.h, fontSize: 17 }}>{job?.room?.name_th}{jobDone ? '' : ` · ${isMidstay ? 'บริการระหว่างพัก' : isPrep ? 'เตรียมห้อง' : 'ต้อนรับลูกค้า'}`}</h1>
          <div style={S.sub}>{jobDone ? 'งานนี้ส่งแล้ว — ดูรายละเอียด/ประวัติ' : (isMidstay ? 'เลือกระดับงานที่ทำ แล้วกดส่ง' : isPrep ? 'แตะ "ไม่" เฉพาะข้อที่มีปัญหา' : 'ทำครบแล้วกดยืนยัน')}</div>
        </div>
      </div>

      {/* รายละเอียดการเข้าพัก */}
      {job?.booking && (
        <div style={{ ...S.card, marginBottom: 12, background: '#F1F7F3' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>
            👥 ผู้เข้าพัก {guestQty} ท่าน
            {additionalQty > 0 && <span style={{ color: '#B26B0E' }}> ❗️เสริม {additionalQty} (รวม {totalQty})</span>}
          </div>
          {b.contact_name && <div style={{ fontSize: 14, color: '#586159', marginTop: 4 }}>👤 {b.contact_name} {b.contact_tel_1 || ''}</div>}
          {bNote && <div style={{ fontSize: 14, color: '#B26B0E', marginTop: 4 }}>❗️ {bNote}</div>}
        </div>
      )}

      {/* midstay: ระดับที่ทำ (ส่งแล้ว) / ตัวเลือกระดับ (ยังไม่ส่ง) */}
      {isMidstay && jobDone && (
        <div style={{ ...S.card, marginBottom: 10, background: '#F1F7F3' }}>
          <div style={{ fontWeight: 800 }}>🧹 ระดับที่ทำ: {midstayLevelLabel(job?.service_level)}</div>
          {job?.cleaner_note && <div style={{ fontSize: 13, color: '#586159', marginTop: 4 }}>📝 {job.cleaner_note}</div>}
        </div>
      )}
      {isMidstay && !jobDone && (
        <div style={{ ...S.card, marginBottom: 10 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>เลือกระดับงานที่ทำ</div>
          {MIDSTAY_LEVELS.map((lv) => {
            const on = midstayLevel === lv.v;
            return (
              <button key={lv.v} onClick={() => setMidstayLevel(lv.v)}
                style={{ boxSizing: 'border-box', width: '100%', textAlign: 'left', border: on ? '2px solid #0F8A56' : '1.5px solid #DBE2DC', background: on ? '#EAF6EF' : '#fff', borderRadius: 12, padding: 14, marginBottom: 8, fontSize: 15, fontWeight: 700, color: '#1A211D' }}>
                <span style={{ marginRight: 8 }}>{on ? '🟢' : '⚪️'}</span>{lv.v}. {lv.label}
              </button>
            );
          })}
          <input value={midstayNote} onChange={(e) => setMidstayNote(e.target.value)} placeholder="โน้ตถึงแอดมิน เช่น ลูกค้าขอแค่เก็บขยะ"
            style={{ boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 11, fontSize: 15, marginTop: 4 }} />
        </div>
      )}

      {/* ประวัติที่เช็คแล้ว (read-only + รูป) */}
      {history.length > 0 && ['prep', 'checkin', 'midstay'].map((ph) => {
        const hitems = history.filter((c) => c.phase === ph);
        if (!hitems.length) return null;
        return (
          <div key={ph} style={{ ...S.card, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{ph === 'prep' ? '🧹 เตรียมห้อง (ส่งแล้ว)' : ph === 'checkin' ? '🛎️ ต้อนรับลูกค้า (ส่งแล้ว)' : '🧹 รายการที่ทำ (ส่งแล้ว)'}</div>
            {hitems.map((c) => (
              <div key={c.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderTop: '1px solid #EEF1EE' }}>
                <span>{c.result === 'fail' ? '⚠️' : '✅'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: c.result === 'fail' ? '#B26B0E' : '#1A211D' }}>{c.label_snapshot}</div>
                  {c.note && <div style={{ fontSize: 13, color: '#586159' }}>{c.note}</div>}
                  {c.photo_url && <a href={c.photo_url} target="_blank" rel="noreferrer"><img src={c.photo_url} alt="" style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 8, marginTop: 4 }} /></a>}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {jobDone && (
        <div style={{ textAlign: 'center', fontSize: 14, color: '#0F8A56', fontWeight: 700, marginTop: 10 }}>✅ ส่งงานเรียบร้อยแล้ว</div>
      )}

      {!jobDone && !isMidstay && template.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ยังไม่มีรายการเช็ค (ตั้งค่าใน CMS)</div>}
      {!jobDone && isMidstay && midstayLevel && visibleTemplate.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ระดับนี้ไม่มีรายการเช็ค — กดส่งได้เลย</div>}

      {!jobDone && visibleTemplate.map((it) => {
        const c = checks[it.id] || { result: 'pass' };
        const fail = c.result === 'fail';
        return (
          <div key={it.id} style={{ ...S.card, marginBottom: 10, borderColor: fail ? '#B26B0E' : '#DBE2DC', background: fail ? '#FBEED4' : '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, fontSize: 17, fontWeight: 700 }}>{it.label}</div>
              <div style={S.toggleWrap}>
                <button style={S.tg(!fail, 'pass')} onClick={() => setResult(it.id, 'pass')}>✓</button>
                <button style={S.tg(fail, 'fail')} onClick={() => setResult(it.id, 'fail')}>ไม่</button>
              </div>
            </div>
            {fail && (
              <div style={{ marginTop: 10 }}>
                <input value={c.note || ''} onChange={(e) => setNote(it.id, e.target.value)} placeholder="พิมพ์สั้นๆ เช่น มดขึ้นที่นอน"
                  style={{ boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 11, fontSize: 15 }} />
                <label style={{ display: 'block', marginTop: 8, border: '1.5px dashed #CBD4CC', borderRadius: 10, padding: 11, fontSize: 14, fontWeight: 700, color: c.photo_url ? '#0F8A56' : '#586159', textAlign: 'center' }}>
                  {uploadingId === it.id ? 'กำลังอัปโหลด…' : c.photo_url ? '📷 แนบรูปแล้ว ✓ (แตะเปลี่ยน)' : '📷 ถ่าย/แนบรูป'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onPickPhoto(it.id, e.target.files[0])} />
                </label>
              </div>
            )}
          </div>
        );
      })}

      {!jobDone && (
        <>
          <button disabled={busy || (isMidstay && !midstayLevel)}
            style={S.bigBtn(isMidstay ? (midstayLevel ? '#0F8A56' : '#8A938B') : (isPrep && anyFail ? '#B26B0E' : '#0F8A56'))} onClick={submit}>
            {busy ? 'กำลังส่ง…' : isMidstay ? (midstayLevel ? `✅ ส่งงาน (ระดับ ${midstayLevel})` : 'เลือกระดับก่อน') : isPrep ? (anyFail ? '⚠️ แจ้งปัญหา + ส่งงานห้องพร้อม' : '✅ ห้องพร้อม (ส่งงาน)') : '✅ คอนเฟิร์มส่งงาน'}
          </button>
          <div style={{ textAlign: 'center', fontSize: 12.5, color: '#8A938B', marginTop: 8 }}>
            {job?.wage_type === 'monthly'
              ? (isPrep && anyFail ? 'ปัญหาจะถูกส่งให้แอดมิน' : 'ส่งรายงานให้แอดมินในกลุ่มไลน์')
              : isMidstay ? 'แอดมินจะตรวจ + ตั้งค่าจ้างตามระดับ' : isPrep && anyFail ? 'ปัญหาจะถูกส่งให้แอดมิน — ค่าจ้างไม่ถูกตัด' : 'กดปุ่มเดียวจบ'}
          </div>
        </>
      )}
    </div>
  );
}
